import React, { useCallback, useEffect, useMemo } from 'react';
import { type MessageDescriptor, defineMessages, useIntl } from 'react-intl-next';
import gql from 'graphql-tag';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import ShareIcon from '@atlaskit/icon/core/share';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { User } from '@atlaskit/user-picker';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { HeadingItem, Section, SkeletonHeadingItem, SkeletonItem } from '@atlaskit/menu';

import { GlobalInviteWithLazyLoadedRelayProvider } from '@atlassian/global-invite';

import { CopyLinkButton } from '@confluence/copy-content-link/entry-points/CopyLinkButton';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useEditorNetworkStatus } from '@confluence/editor-network-status';
import { useUserSuggestions } from '@confluence/share-and-restrict-dialog/entry-points/useUserSuggestions';
import { useQueryInterop } from '@confluence/apollo-relay-interop';

import type { PresenceContentMode, PresenceContentType } from '../presenceTypes';

import { SuggestedCollaboratorsDialogMenu } from './SuggestedCollaboratorsDialogMenu';
import type {
	SuggestedCollaboratorsDialogsViewRestrictionsQuery as SuggestedCollaboratorsDialogsViewRestrictionsQueryType,
	SuggestedCollaboratorsDialogsViewRestrictionsQueryVariables,
} from './__types__/suggestedCollaboratorsDialogsViewRestrictionsQuery';
import type {
	SuggestedCollaboratorsDialogInvitePermissionsQuery as SuggestedCollaboratorsDialogInvitePermissionsQueryType,
	SuggestedCollaboratorsDialogInvitePermissionsQueryVariables,
} from './__types__/SuggestedCollaboratorsDialogInvitePermissionsQuery';

const i18n = defineMessages({
	// Suggested collaborators strings
	suggestedCollaboratorsDescription: {
		id: 'team-presence.menu.empty-state.suggestions-dialog.description',
		defaultMessage: 'You often work together',
		description:
			'Text shown when the user opens the suggested collaborators dialog and there are collaborators, that informs them they work together.',
	},
	copyLinkButtonText: {
		id: 'team-presence.menu.empty-state.suggested-collaborators.footer.copy-button',
		defaultMessage: 'Copy link',
		description: 'Call to action button to copy the link to the content.',
	},
	moreShareOptionsButtonText: {
		id: 'team-presence.menu.empty-state.suggested-collaborators.footer.more-share-options-button',
		defaultMessage: 'More share options',
		description: 'Call to action button to share the content with others.',
	},

	// Invite dialog strings
	noCollaboratorsCanInviteOnPageDescription: {
		id: 'team-presence.menu.empty-state.no-collaborators.with-invite-permissions.on-page.description',
		defaultMessage: 'Want to loop in others? Invite teammates to check this page out.',
		description:
			'Text shown when the user opens the suggested collaborators dialog on a page and there are no collaborators but they have invite permissions, which informs them they can invite teammates.',
	},
	noCollaboratorsCanInviteOnBlogpostDescription: {
		id: 'team-presence.menu.empty-state.no-collaborators.with-invite-permissions.on-blogpost.description',
		defaultMessage: 'Want to loop in others? Invite teammates to check this blog post out.',
		description:
			'Text shown when the user opens the suggested collaborators dialog on a blog post and there are no collaborators but they have invite permissions, which informs them they can invite teammates.',
	},
	noCollaboratorsCanInviteOnDatabaseDescription: {
		id: 'team-presence.menu.empty-state.no-collaborators.with-invite-permissions.on-database.description',
		defaultMessage: 'Want to loop in others? Invite teammates to check this database out.',
		description:
			'Text shown when the user opens the suggested collaborators dialog on a database and there are no collaborators but they have invite permissions, which informs them they can invite teammates.',
	},
	noCollaboratorsCanInviteOnWhiteboardDescription: {
		id: 'team-presence.menu.empty-state.no-collaborators.with-invite-permissions.on-whiteboard.description',
		defaultMessage: 'Want to loop in others? Invite teammates to check this whiteboard out.',
		description:
			'Text shown when the user opens the suggested collaborators dialog on a whiteboard and there are no collaborators but they have invite permissions, which informs them they can invite teammates.',
	},
	noCollaboratorsCanInviteLiveDocDescription: {
		id: 'team-presence.menu.empty-state.no-collaborators.with-invite-permissions.on-live-doc.description',
		defaultMessage: 'Want to loop in others? Invite teammates to check this live doc out.',
		description:
			'Text shown when the user opens the suggested collaborators dialog on a live doc and there are no collaborators but they have invite permissions, which informs them they can invite teammates.',
	},
	inviteTeammatesButtonText: {
		id: 'team-presence.menu.empty-state.no-collaborators.footer.invite-teammates-button',
		defaultMessage: 'Add people to Confluence',
		description: 'Call to action button to invite others to the site.',
	},

	// Generic share strings
	noCollaboratorsCannotInviteDescription: {
		id: 'team-presence.menu.empty-state.no-collaborators.without-invite-permissions.description',
		defaultMessage: 'Want to loop in others? Quickly share this with relevant teammates.',
		description:
			'Text shown when the user opens the suggested collaborators dialog, there are no collaborators and they do not have invite permissions that informs them they can share this content.',
	},
	sharePageButtonText: {
		id: 'team-presence.menu.empty-state.no-collaborators.footer.share-page-button',
		defaultMessage: 'Share page',
		description: 'Call to action button to share the page.',
	},
	shareBlogpostButtonText: {
		id: 'team-presence.menu.empty-state.no-collaborators.footer.share-blogpost-button',
		defaultMessage: 'Share blog post',
		description: 'Call to action button to share the blog post.',
	},
	shareDatabaseButtonText: {
		id: 'team-presence.menu.empty-state.no-collaborators.footer.share-database-button',
		defaultMessage: 'Share database',
		description: 'Call to action button to share the database.',
	},
	shareWhiteboardButtonText: {
		id: 'team-presence.menu.empty-state.no-collaborators.footer.share-whiteboard-button',
		defaultMessage: 'Share whiteboard',
		description: 'Call to action button to share the whiteboard.',
	},
	shareLiveDocButtonText: {
		id: 'team-presence.menu.empty-state.no-collaborators.footer.share-live-doc-button',
		defaultMessage: 'Share live doc',
		description: 'Call to action button to share the live doc.',
	},

	// Generic strings when share is not available
	noCollaboratorsCannotInviteCannotShareDescription: {
		id: 'team-presence.menu.empty-state.no-collaborators.without-invite-permissions.share-not-available.description',
		defaultMessage: 'Want to loop in others? Copy the link to share this with relevant teammates.',
		description:
			'Text shown when the user opens the suggested collaborators dialog, there are no collaborators they do not have invite permissions and they are restricted from sharing that informs them they can share this content via the link.',
	},
});

const CONTEXT_TYPE = 'team-presence:suggested-collaborators-list:suggested-share-users';
export const MAX_LOADING_ITEMS = 5;
export const SUGGESTED_COLLABORATORS_DIALOG_SOURCE = 'collabInTeamPresenceExperiment';

export type SuggestedUser = {
	id: string;
	name: string;
	picture: string;
	status: string;
};

type ShareDialogTextType = Record<
	string,
	{
		modalDescription: string;
		buttonName: string;
	}
>;

const styles = cssMap({
	suggestedUsersDialogHeaderStyles: {
		marginTop: token('space.250'),
		marginBottom: token('space.075'),
	},
	generalHeaderStyles: {
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.200'),
	},
	suggestedUsersDialogFooterStyles: {
		padding: token('space.100'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
	generalDialogFooterStyles: {
		padding: token('space.200'),
	},
});

export type SuggestedCollaboratorsDialogProps = {
	contentMode: PresenceContentMode;
	contentId: string;
	contentType: PresenceContentType;
	onShareClick: (analyticsSource: string) => void;
	spaceKey: string;
	testId?: string;
};

const contentTypeToTranslationMap = (
	formatMessage: (descriptor: MessageDescriptor) => string,
): ShareDialogTextType => {
	return {
		blogpost: {
			modalDescription: formatMessage(i18n.noCollaboratorsCanInviteOnBlogpostDescription),
			buttonName: formatMessage(i18n.shareBlogpostButtonText),
		},
		page: {
			modalDescription: formatMessage(i18n.noCollaboratorsCanInviteOnPageDescription),
			buttonName: formatMessage(i18n.sharePageButtonText),
		},
		live: {
			modalDescription: formatMessage(i18n.noCollaboratorsCanInviteLiveDocDescription),
			buttonName: formatMessage(i18n.shareLiveDocButtonText),
		},
		whiteboard: {
			modalDescription: formatMessage(i18n.noCollaboratorsCanInviteOnWhiteboardDescription),
			buttonName: formatMessage(i18n.shareWhiteboardButtonText),
		},
		database: {
			modalDescription: formatMessage(i18n.noCollaboratorsCanInviteOnDatabaseDescription),
			buttonName: formatMessage(i18n.shareDatabaseButtonText),
		},
	};
};

const SuggestedCollaboratorsDialogViewRestrictionsQuery = gql`
	query SuggestedCollaboratorsDialogsViewRestrictionsQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			hasViewRestrictions
			ancestors {
				id
				hasViewRestrictions
			}
		}
	}
`;

const SuggestedCollaboratorsDialogInvitePermissionsQuery =
	// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
	gql`
		query SuggestedCollaboratorsDialogInvitePermissionsQuery($resourceARI: String!) @experimental {
			experimentalInvitePermissions(resourceARI: $resourceARI) {
				directInviteMode
				invitePendingApprovalMode
			}
		}
	`;

export const SuggestedCollaboratorsDialogComponent = ({
	contentMode,
	contentId,
	contentType,
	onShareClick,
	spaceKey,
	flags,
	testId,
}: SuggestedCollaboratorsDialogProps & WithFlagsProps) => {
	const { formatMessage } = useIntl();
	const { cloudId, orgId, userId } = useSessionData();
	const { isNetworkDisabled } = useEditorNetworkStatus();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const currentPickedPrincipalIds = useMemo(() => [userId ?? ''], [userId]);

	const { suggestedUsers, loading, error } = useUserSuggestions({
		currentPickedPrincipalIds,
		maxDisplayResults: 10,
		shouldFetchMoreResults: false,
		contextType: CONTEXT_TYPE,
		siteId: cloudId,
		orgId,
		source: SUGGESTED_COLLABORATORS_DIALOG_SOURCE,
	});

	const {
		data: restrictionsData,
		error: restrictionsError,
		loading: restrictionsLoading,
	} = useQueryInterop<
		SuggestedCollaboratorsDialogsViewRestrictionsQueryType,
		SuggestedCollaboratorsDialogsViewRestrictionsQueryVariables
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	>(SuggestedCollaboratorsDialogViewRestrictionsQuery, { variables: { contentId } });

	// Check if the content has view restrictions or the parent content has view restrictions
	const hasViewRestrictions = !!(
		restrictionsData?.singleContent?.hasViewRestrictions ||
		restrictionsData?.singleContent?.ancestors?.some((ancestor) => {
			return ancestor?.hasViewRestrictions === true;
		})
	);

	/*
	 *  The share button is disabled if the network is disabled.
	 */
	const isShareButtonDisabled = isNetworkDisabled;

	// Skip invite query if there are suggested users and share button is enabled or has view restrictions
	const skipInviteQuery =
		(Boolean(suggestedUsers?.length) && !isShareButtonDisabled) || hasViewRestrictions;

	const {
		data: inviteData,
		error: inviteError,
		loading: inviteLoading,
	} = useQueryInterop<
		SuggestedCollaboratorsDialogInvitePermissionsQueryType,
		SuggestedCollaboratorsDialogInvitePermissionsQueryVariables
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	>(SuggestedCollaboratorsDialogInvitePermissionsQuery, {
		variables: {
			resourceARI: `ari:cloud:platform::site/${cloudId}`,
		},
		skip: skipInviteQuery,
	});

	const userRecommendations: (SuggestedUser | undefined)[] = (suggestedUsers || []).map(
		(suggestion: User | null): SuggestedUser | undefined => {
			if (suggestion !== null) {
				return {
					id: suggestion.id,
					name: suggestion.name,
					status: suggestion.type || '',
					picture: suggestion.avatarUrl || '',
				};
			}
			return;
		},
	);
	const suggestedUsersList: SuggestedUser[] = userRecommendations.filter(
		(user: SuggestedUser | undefined): user is SuggestedUser => user !== undefined,
	);

	// Determine if the user is in edit mode. Live pages supports both edit and view modes, so
	// we need to do an additional check to see if we're in edit mode on a live pages.
	const isInEditMode = contentMode === 'edit' || (contentMode === 'live' && isLiveEditMode);
	const pageMode: PresenceContentMode = isInEditMode ? 'edit' : 'view';

	const translations =
		contentTypeToTranslationMap(formatMessage)[
			contentType === 'page' && contentMode === 'live' ? 'live' : contentType
		];

	// Show suggested collaborator dialog if share button enabled, there are suggested collaborators, and the content has no view restrictions
	const showSuggestedUsersDialog =
		!isShareButtonDisabled && !!suggestedUsersList?.length && !hasViewRestrictions;
	const buttonAppearance = showSuggestedUsersDialog ? 'subtle' : 'default';

	const showInviteButton =
		!inviteError && inviteData?.experimentalInvitePermissions?.directInviteMode === 'ANYONE';

	const getCurrentDialog = () => {
		if (showSuggestedUsersDialog) {
			return 'suggestedCollaboratorsDialog';
		} else if (showInviteButton) {
			return 'noCollaboratorsInviteDialog';
		} else if (!isShareButtonDisabled) {
			return 'noCollaboratorsShareDialog';
		}
		return 'noCollaboratorsDefaultDialog';
	};
	const currentDialog = getCurrentDialog();

	const onShare = useCallback(
		(_: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			if (onShareClick) {
				onShareClick('suggestedCollaboratorsDialog');

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'shareWithCollaboratorsButton',
						source: currentDialog,
						attributes: {
							contentId,
							contentType,
							contentMode,
						},
					},
				}).fire();
			}
		},
		[onShareClick, contentId, contentType, contentMode, createAnalyticsEvent, currentDialog],
	);

	const headerText = () => {
		if (showSuggestedUsersDialog) {
			return formatMessage(i18n.suggestedCollaboratorsDescription);
		} else if (showInviteButton) {
			return translations.modalDescription;
		} else if (!isShareButtonDisabled) {
			return formatMessage(i18n.noCollaboratorsCannotInviteDescription);
		} else {
			return formatMessage(i18n.noCollaboratorsCannotInviteCannotShareDescription);
		}
	};

	useEffect(() => {
		if (!loading && !inviteLoading && !restrictionsLoading) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: currentDialog,
					attributes: {
						contentMode,
						contentType,
						contentId,
						suggestedUsersCount: suggestedUsersList?.length,
					},
				},
			}).fire();
		}
	}, [
		loading,
		inviteLoading,
		restrictionsLoading,
		contentMode,
		contentType,
		contentId,
		suggestedUsersList,
		createAnalyticsEvent,
		currentDialog,
	]);

	if (loading || inviteLoading || restrictionsLoading) {
		return (
			<Section>
				<SkeletonHeadingItem isShimmering />
				{Array.from({ length: MAX_LOADING_ITEMS }).map((_, index) => (
					<SkeletonItem
						key={`skeleton-item-${index}`}
						testId={`skeleton-item-${index}`}
						hasAvatar
						isShimmering
					/>
				))}
			</Section>
		);
	}

	const queryError = error || inviteError || restrictionsError;

	// Only firing event for inviteError as we don't have an easy way to track these errors and attribute them to our experiment yet
	if (inviteError) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: SUGGESTED_COLLABORATORS_DIALOG_SOURCE,
				action: 'errored',
				actionSubject: 'suggestedCollaboratorsDialogInviteQuery',
				attributes: {
					contentId,
					contentMode,
					contentType,
					error: inviteError,
					message: inviteError?.message,
				},
			},
		}).fire();
	}

	return (
		<Stack
			role="dialog"
			// TODO: Update the attribute below so it is translated.
			// eslint-disable-next-line no-restricted-syntax
			aria-label="suggested-collaborators-dialog"
			aria-describedby="suggested-collaborators-modal-description"
			testId={testId}
		>
			{queryError && <ErrorDisplay error={queryError} />}
			{showSuggestedUsersDialog ? (
				<>
					<Box xcss={styles.suggestedUsersDialogHeaderStyles}>
						<HeadingItem>{headerText()}</HeadingItem>
					</Box>
					<SuggestedCollaboratorsDialogMenu
						contentId={contentId}
						contentType={contentType}
						contentMode={pageMode}
						pageMode={pageMode}
						suggestedUsers={suggestedUsersList}
					/>
				</>
			) : (
				<Box xcss={styles.generalHeaderStyles} id="suggested-collaborators-modal-description">
					{headerText()}
				</Box>
			)}
			<Inline
				xcss={
					showSuggestedUsersDialog
						? styles.suggestedUsersDialogFooterStyles
						: styles.generalDialogFooterStyles
				}
				alignInline={showSuggestedUsersDialog ? 'start' : 'end'}
			>
				{showSuggestedUsersDialog || !showInviteButton ? (
					<Inline space="space.100">
						<CopyLinkButton
							buttonAppearance={buttonAppearance}
							buttonText={formatMessage(i18n.copyLinkButtonText)}
							contentId={contentId}
							contentType={contentType}
							pageMode={pageMode}
							spaceKey={spaceKey}
							source="suggestedCollaboratorsDialog"
							onClickAttributes={{
								contentId,
								contentMode,
								contentType,
								dialogSelected: currentDialog,
							}}
						/>
						{!isShareButtonDisabled && (
							<Button
								iconBefore={ShareIcon}
								testId="open-share-dialog"
								onClick={onShare}
								appearance={buttonAppearance}
							>
								{showSuggestedUsersDialog
									? formatMessage(i18n.moreShareOptionsButtonText)
									: translations.buttonName}
							</Button>
						)}
					</Inline>
				) : (
					<GlobalInviteWithLazyLoadedRelayProvider product="confluence" showFlag={flags.showFlag}>
						<Inline space="space.075" alignBlock="center">
							<AddIcon label="invite-button-icon" />
							{formatMessage(i18n.inviteTeammatesButtonText)}
						</Inline>
					</GlobalInviteWithLazyLoadedRelayProvider>
				)}
			</Inline>
		</Stack>
	);
};

export const SuggestedCollaboratorsDialog = withFlags(SuggestedCollaboratorsDialogComponent);
